/*
 * NORA PRO chemical compatibility engine.
 * One copy of the lookup and list logic, used by:
 *   - the guide page tool (theme asset assets/nora-chem-engine.js, copied by build.mjs)
 *   - the lookup tests (tools/chem-guide/test-lookups.mjs)
 *   - the assistant Worker (workers/chem-assistant), which only maps words to row ids
 * The dataset decides every answer. Nothing in here invents a rating.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NoraChem = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SPELL = {
    sulfuric: 'sulphuric', sulfate: 'sulphate', sulfur: 'sulphur', aluminum: 'aluminium', tire: 'tyre',
    tires: 'tyres', carburetor: 'carburettor', gasoline: 'petrol', color: 'colour', liter: 'litre',
    liters: 'litres',
  };
  var FAMILY_ORDER = ['acid', 'solvent', 'alkaline'];
  var STOP = ['the', 'for', 'and', 'of', 'to', 'my', 'in', 'is', 'are', 'can', 'does', 'with', 'which', 'what', 'seal', 'seals',
    'sprayer', 'spray', 'spraying', 'use', 'using', 'need', 'needs', 'do', 'how', 'good', 'suit', 'suits', 'work', 'works', 'will',
    'please', 'about', 'best', 'right', 'type', 'strength', 'percent', 'cent', 'per', 'on', 'it', 'its', 'an', 'at', 'be'];

  function norm(s) {
    s = String(s == null ? '' : s).toLowerCase();
    if (s.normalize) s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
    s = s.replace(/&/g, ' and ').replace(/[^a-z0-9%.]+/g, ' ').replace(/(^|\s)\.+|\.+(\s|$)/g, ' ');
    return s.replace(/\s+/g, ' ').trim();
  }

  function spell(s) {
    return norm(s).split(' ').map(function (t) { return SPELL[t] || t; }).join(' ');
  }

  function lev(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > 2) return 3;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j += 1) prev[j] = j;
    for (i = 1; i <= al; i += 1) {
      cur = [i];
      for (j = 1; j <= bl; j += 1) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
      }
      prev = cur;
    }
    return prev[bl];
  }

  // Names we match against: the display name, the name without brackets, each bracket phrase, each alias.
  function labelsOf(row) {
    var out = [row.name];
    var plain = row.name.replace(/\([^)]*\)/g, ' ');
    out.push(plain);
    var m = row.name.match(/\(([^)]*)\)/g) || [];
    m.forEach(function (b) { out.push(b.replace(/[()]/g, '')); });
    (row.aka || []).forEach(function (a) { out.push(a); });
    var seen = {};
    return out.map(spell).filter(function (x) { if (!x || seen[x]) return false; seen[x] = 1; return true; });
  }

  function buildIndex(data) {
    if (data._index) return data._index;
    var idx = data.chemicals.map(function (row) {
      var labels = labelsOf(row);
      var tokens = {};
      labels.forEach(function (l) { l.split(' ').forEach(function (t) { if (t.length > 1) tokens[t] = 1; }); });
      return { row: row, labels: labels, tokens: Object.keys(tokens) };
    });
    Object.defineProperty(data, '_index', { value: idx, enumerable: false });
    return idx;
  }

  // Pull a trailing strength out of free text, for example "hydrochloric acid 10%" or "bleach 12.5 percent".
  function parseQuery(q) {
    var text = String(q == null ? '' : q);
    var strength = null;
    var m = text.match(/(\d+(?:\.\d+)?)\s*(%|per\s*cent|percent)/i);
    if (m) {
      strength = parseFloat(m[1]);
      text = text.replace(m[0], ' ');
    }
    var ph = text.match(/\bph\s*(\d+(?:\.\d+)?)/i);
    var phValue = null;
    if (ph) {
      phValue = parseFloat(ph[1]);
      text = text.replace(ph[0], ' ');
    }
    return { text: text.trim(), strength: strength, ph: phValue };
  }

  function score(entry, q, qTokens) {
    var best = 0;
    var i;
    for (i = 0; i < entry.labels.length; i += 1) {
      var l = entry.labels[i];
      if (l === q) return 100;
      var startsWith = l.indexOf(q + ' ') === 0;
      var endsWith = l.length > q.length && l.lastIndexOf(' ' + q) === l.length - q.length - 1;
      if (startsWith || endsWith) best = Math.max(best, 80);
      else if (q.length >= 3 && l.indexOf(q) > -1) best = Math.max(best, 55);
    }
    if (best >= 80) return best;
    // Every word typed must be found in the row. A partial overlap is never a match, so "sodium chlorate"
    // can not land on "sodium hypochlorite".
    var hit = 0;
    qTokens.forEach(function (t) { if (entry.tokens.indexOf(t) > -1) hit += 1; });
    if (qTokens.length && hit === qTokens.length) best = Math.max(best, 65);
    if (best < 45) {
      // Typos: one slip in each long word, nothing looser.
      var long = qTokens.filter(function (t) { return t.length >= 5; });
      var fuzzy = 0;
      long.forEach(function (t) {
        for (var k = 0; k < entry.tokens.length; k += 1) {
          if (entry.tokens[k].length >= 5 && lev(t, entry.tokens[k]) <= 1) { fuzzy += 1; break; }
        }
      });
      var short = qTokens.filter(function (t) { return t.length < 5; });
      var shortHit = short.filter(function (t) { return entry.tokens.indexOf(t) > -1; }).length;
      if (long.length && fuzzy === long.length && shortHit === short.length) best = Math.max(best, 45);
    }
    return best;
  }

  // Ranked matches for free text. Returns [{row, score}] with score >= 40.
  function search(data, query, limit) {
    var parsed = parseQuery(query);
    var q = spell(parsed.text);
    if (q.length < 2) return [];
    var qTokens = q.split(' ').filter(function (t) { return t.length > 1 && STOP.indexOf(t) === -1; });
    var idx = buildIndex(data);
    var out = [];
    idx.forEach(function (entry) {
      var s = score(entry, q, qTokens.length ? qTokens : [q]);
      if (s >= 40) out.push({ row: entry.row, score: s });
    });
    out.sort(function (a, b) { return b.score - a.score || a.row.name.length - b.row.name.length; });
    return out.slice(0, limit || 8);
  }

  // Matches (score 65 and up) are safe to show as the answer. Suggestions (45 to 64) are typo guesses
  // and must be offered as "did you mean", never shown as an answer.
  function lookup(data, query, limit) {
    var all = search(data, query, limit || 8);
    return {
      parsed: parseQuery(query),
      matches: all.filter(function (x) { return x.score >= 65; }),
      suggestions: all.filter(function (x) { return x.score < 65; }),
    };
  }

  // True when the top match is clearly the one the user meant (no close runner up).
  function isClear(result) {
    var m = result.matches;
    if (!m.length) return false;
    return m.length === 1 || m[0].score - m[1].score >= 15;
  }

  function byId(data, id) {
    for (var i = 0; i < data.chemicals.length; i += 1) if (data.chemicals[i].id === id) return data.chemicals[i];
    return null;
  }

  // The answer for one row. opts: { strength: number|null, hot: boolean }
  function resolve(data, row, opts) {
    opts = opts || {};
    var res = {
      id: row.id, name: row.name, group: row.group,
      rating: row.rating, fams: row.rating === 'ask' ? [] : (row.fams || []).slice(),
      seal: row.seal, sealGroup: row.sealGroup, spec: row.spec || null, text: null,
      max: row.max || null, col: row.col, note: row.note || null,
      needsStrength: false, scale: row.scale || null, hot: false, strength: null,
    };
    if (row.bands && row.bands.length) {
      var s = typeof opts.strength === 'number' && !isNaN(opts.strength) ? opts.strength : null;
      if (s === null) {
        // A cap row is rated as it stands; its single band only matters above the top strength.
        res.needsStrength = !row.cap;
      } else {
        res.strength = s;
        var band = null;
        for (var i = 0; i < row.bands.length; i += 1) {
          if (s <= row.bands[i].upto) { band = row.bands[i]; break; }
        }
        if (band) {
          res.rating = band.rating;
          res.fams = band.rating === 'ask' ? [] : (band.fams || []).slice();
          res.spec = band.spec || null;
          res.text = band.text;
        } else {
          res.rating = 'ask';
          res.fams = [];
          res.text = row.over || 'No source rates a sprayer at this strength. Ask us.';
        }
      }
    }
    if (opts.hot) {
      res.hot = true;
      res.rating = 'ask';
      res.fams = [];
      res.text = data.meta.temperature_note;
    }
    res.famLabels = res.fams.map(function (f) { return data.families[f].label; });
    return res;
  }

  function subsets(arr) {
    var out = [];
    for (var mask = 1; mask < (1 << arr.length); mask += 1) {
      var s = [];
      for (var i = 0; i < arr.length; i += 1) if (mask & (1 << i)) s.push(arr[i]);
      out.push(s);
    }
    out.sort(function (a, b) { return a.length - b.length; });
    return out;
  }

  // Work out how many sprayers a chemical list needs. items: [{row, strength, hot}]
  function planList(data, items) {
    var resolved = items.map(function (it) {
      var r = resolve(data, it.row, { strength: it.strength, hot: it.hot });
      r.input = it;
      return r;
    });
    var asks = resolved.filter(function (r) { return !r.fams.length; });
    var covered = resolved.filter(function (r) { return r.fams.length; });
    var units = [];
    if (covered.length) {
      var best = null;
      subsets(FAMILY_ORDER).forEach(function (set) {
        if (best && set.length > best.length) return;
        var ok = covered.every(function (r) { return r.fams.some(function (f) { return set.indexOf(f) > -1; }); });
        if (!ok) return;
        if (!best) { best = set; return; }
        var groups = function (x) { var g = {}; x.forEach(function (f) { g[data.families[f].sealGroup] = 1; }); return Object.keys(g).length; };
        if (set.length === best.length && groups(set) < groups(best)) best = set;
      });
      units = best || [];
    }
    covered.forEach(function (r) {
      var pick = r.fams.filter(function (f) { return units.indexOf(f) > -1; });
      r.assigned = pick.length ? pick[0] : r.fams[0];
    });
    var sealGroups = {};
    units.forEach(function (f) { sealGroups[data.families[f].sealGroup] = 1; });
    var sealList = Object.keys(sealGroups);
    var mixesSeals = sealList.length > 1;
    var conditional = resolved.filter(function (r) { return r.rating === 'conditional' || r.needsStrength; });
    var messages = [];
    var summary = '';
    if (!resolved.length) messages.push('Add the chemicals you spray.');
    if (units.length === 1) summary = 'One sprayer covers the chemicals we can match: the ' + data.families[units[0]].label.toLowerCase() + '.';
    if (units.length > 1) summary = 'You need ' + units.length + ' sprayers: ' + units.map(function (f) { return 'the ' + data.families[f].label.toLowerCase(); }).join(', ') + '.';
    if (mixesSeals) messages.push('This list mixes VITON and EPDM chemicals, so the seals cannot be shared. Keep each chemical in its own sprayer and rinse between uses.');
    else if (units.length > 1) messages.push('Both sprayers use VITON seals, but the acid and solvent models are different builds. A VITON acid sprayer is not for petroleum solvents, and the reverse.');
    if (asks.length) messages.push(asks.length + (asks.length === 1 ? ' chemical needs' : ' chemicals need') + ' a check with us before you buy: ' + asks.map(function (r) { return r.name; }).join(', ') + '.');
    if (conditional.length) messages.push('Some answers are conditional or need a strength. Check the notes below each one.');
    return {
      items: resolved, asks: asks, covered: covered, units: units,
      unitLabels: units.map(function (f) { return data.families[f].label; }),
      sealGroups: sealList, mixesSeals: mixesSeals, messages: messages, summary: summary,
      oneSprayer: units.length === 1 && !asks.length,
    };
  }

  // Share link payload: "hydrochloric-acid:10,xylene,bleach". Temperature goes in a separate flag.
  function encodeList(entries) {
    return entries.map(function (e) { return e.row.id + (typeof e.strength === 'number' && !isNaN(e.strength) ? ':' + e.strength : ''); }).join(',');
  }
  function decodeList(data, str) {
    var out = [];
    String(str || '').split(',').slice(0, 20).forEach(function (part) {
      var bits = part.split(':');
      var row = byId(data, bits[0].replace(/[^a-z0-9-]/g, ''));
      if (!row) return;
      var s = bits[1] === undefined ? null : parseFloat(bits[1]);
      out.push({ row: row, strength: s === null || isNaN(s) ? null : s });
    });
    return out;
  }

  // Quote form prefill. The quote page reads chemical, seal and note.
  function quoteParams(data, plan, hot) {
    var chem = plan.items.map(function (r) {
      return r.name + (r.strength !== null && r.strength !== undefined ? ' (' + r.strength + (r.scale === 'pH' ? ' pH' : '%') + ')' : '');
    }).join('; ');
    var lines = plan.items.map(function (r) {
      var what = r.fams.length ? data.families[r.assigned || r.fams[0]].label : 'ask us' + (r.spec ? ', ' + r.spec : '');
      return r.name + (r.strength !== null && r.strength !== undefined ? ' ' + r.strength + (r.scale === 'pH' ? ' pH' : '%') : '') + ': ' + what;
    });
    var note = 'From the NORA PRO guide (dataset v' + data.meta.version + '). ' + lines.join('. ') + '.' + (hot ? ' The liquid is warmer than 30 °C.' : '');
    return { chemical: chem, seal: plan.sealGroups.join(' and '), note: note };
  }

  // The models we stock for one or more families, grouped. Klager models are rated on the chart. Other
  // brands share the seal but are not Klager's build. Empty when the dataset has no models.
  function modelsFor(data, fams) {
    var list = data.models || [];
    return (fams || []).filter(function (f, i, a) { return a.indexOf(f) === i && data.families[f]; }).map(function (f) {
      var inFam = list.filter(function (m) { return m.fam === f; });
      return {
        fam: f,
        label: data.families[f].label,
        rated: inFam.filter(function (m) { return m.brand === 'Klager'; }),
        other: inFam.filter(function (m) { return m.brand !== 'Klager'; }),
      };
    }).filter(function (g) { return g.rated.length || g.other.length; });
  }

  return {
    norm: norm, spell: spell, parseQuery: parseQuery, search: search, lookup: lookup, isClear: isClear, byId: byId, resolve: resolve,
    planList: planList, encodeList: encodeList, decodeList: decodeList, quoteParams: quoteParams, modelsFor: modelsFor,
    FAMILY_ORDER: FAMILY_ORDER,
  };
});

/*
 * NORA PRO Sprayer Finder (home page and Sprayer Selection Guide).
 * Built into assets/nora-finder.js after engine.js by tools/chem-guide/build.mjs.
 * Two ways in: a few quick questions, or the visitor describes the job in their own words and the
 * assistant Worker (workers/chem-assistant) maps it to row ids. Either way the verified dataset decides
 * the seal and the sprayer, exactly as on the Chemical Compatibility Guide. Model text is never shown.
 * The finder only adds the format question (hand sprayer, foam or mounted) and picks the models we stock.
 */
(function () {
  'use strict';
  if (typeof NoraChem === 'undefined') return;
  var E = NoraChem;

  // Common choices on step 1. Each one is a dataset row, so its answer is the guide's answer.
  var COMMON = [
    ['hydrochloric-acid', 'Hydrochloric acid'], ['descaler', 'Descaler or acid wash'], ['bleach', 'Bleach or chlorine'],
    ['sodium-hydroxide', 'Caustic soda'], ['alkaline-cleaner', 'Alkaline degreaser'], ['truck-fleet-wash', 'Truck wash'],
    ['solvent-degreaser', 'Solvent degreaser'], ['diesel', 'Diesel or fuel'], ['xylene', 'Xylene'],
    ['solvent-sealer', 'Solvent based sealer'], ['plant-protection', 'Weed killer'], ['acetone', 'Acetone'],
  ];

  var FORMATS = [
    { v: 'small', label: 'Small hand sprayer', hint: '1 to 1.5 L, precise work', text: 'small hand sprayer (1 to 1.5 L)' },
    { v: 'large', label: 'Bigger pump sprayer', hint: '5 to 10 L, larger areas', text: 'pump sprayer (5 to 10 L)' },
    { v: 'foam', label: 'Foam', hint: 'Clings to walls, vehicles and equipment', text: 'foam' },
    { v: 'mounted', label: 'Mounted or high volume', hint: 'Wall, drum or IBC, air driven', text: 'mounted or high volume' },
    { v: 'unsure', label: 'Not sure yet', hint: 'Show me the options', text: '' },
  ];
  var FOAM_COL = 'foamers-foam-injection';
  var MOUNTED_COL = 'air-driven-sprayers';

  // Tag Manager keeps the last value of every data layer key, so each event clears the keys it does not set.
  // Same event names and keys as the guide tool, so the existing GTM tag sends them to GA4 unchanged.
  // guide_mode is always "finder", so GA4 can tell the finder apart from the guide page.
  var PARAMS = ['guide_mode', 'guide_chemical', 'guide_rating', 'guide_strength_given', 'guide_list_size', 'guide_via',
    'guide_restored', 'guide_units_needed', 'guide_mixed_seals', 'guide_ask_us', 'guide_kind', 'guide_rows'];
  function track(name, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      for (var i = 0; i < PARAMS.length; i += 1) payload[PARAMS[i]] = undefined;
      payload.guide_mode = 'finder';
      for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break the tool */ }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }
  function sizeOf(m) { var x = String(m.name).match(/(\d+(?:\.\d+)?)\s*L\b/i); return x ? parseFloat(x[1]) : null; }
  function isFoam(m) { return /foam/i.test(m.name); }
  function fmtNum(n) { return String(n); }

  function init(root) {
    var jsonUrl = root.getAttribute('data-json');
    var quoteBase = root.getAttribute('data-quote-url') || '/pages/request-a-quote';
    var guideUrl = root.getAttribute('data-guide-url') || '/pages/chemical-compatibility-guide';
    var body = root.querySelector('[data-sf-body]');
    var tabs = root.querySelectorAll('[data-sf-tab]');
    var aiPane = root.querySelector('[data-sf-pane="ai"]');
    var formPane = root.querySelector('[data-sf-pane="form"]');
    var endpoint = '';
    if (aiPane) {
      endpoint = String(aiPane.getAttribute('data-endpoint') || '').replace(/\/+$/, '');
      if (!/^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(endpoint)) endpoint = ''; // inert unless it is an https address
    }
    if (!body) return;

    var data = null;
    var promise = null;
    var used = false;
    // st: the answers so far. items: [{id, strength, band}] from the dataset. via: form or assistant.
    var st = fresh();
    var history = [];
    var aiBusy = false;

    function fresh() { return { n: 1, items: null, unknown: false, typed: '', strengthAsked: false, format: null, via: 'form', hot: false, aiMessage: '', aiTone: '' }; }
    function snapshot() { return JSON.parse(JSON.stringify(st)); }

    function load() {
      if (data) return Promise.resolve(data);
      if (promise) return promise;
      promise = fetch(jsonUrl).then(function (r) { return r.json(); }).then(function (d) { data = d; return d; });
      promise.catch(function () { promise = null; });
      return promise;
    }

    function firstUse(via) {
      if (used) return;
      used = true;
      track('guide_tool_use', { guide_via: via });
    }

    // ------------------------------------------------------------------ the answer

    function resolved() {
      if (!st.items) return null;
      var items = st.items.map(function (i) { return { row: E.byId(data, i.id), strength: i.strength, hot: st.hot }; })
        .filter(function (i) { return i.row; });
      return E.planList(data, items);
    }

    function needsStrengthStep() {
      if (!st.items || st.items.length !== 1 || st.strengthAsked) return false;
      var row = E.byId(data, st.items[0].id);
      return !!(row && row.bands && row.bands.length && !row.cap && st.items[0].strength === null);
    }

    function needsFormatStep() {
      if (st.format) return false;
      if (st.unknown) return true;
      var plan = resolved();
      return !!(plan && plan.units.length);
    }

    function current() {
      if (!st.items && !st.unknown) return 'chem';
      if (needsStrengthStep()) return 'strength';
      if (needsFormatStep()) return 'format';
      return 'result';
    }

    // The step count is worked out from what is still needed, so it stays honest when a step is skipped.
    function totalSteps() {
      if (current() === 'chem') return 2;
      return st.n + (needsStrengthStep() ? 1 : 0) + (needsFormatStep() ? 1 : 0) - 1;
    }

    // ------------------------------------------------------------------ steps

    function stepHead(q) {
      var n = st.n;
      var total = Math.max(totalSteps(), n);
      return '<p class="sf__prog">Step ' + n + ' of ' + total + '</p>' +
        '<div class="sf__bar" aria-hidden="true"><span style="width:' + Math.round((n - 1) / total * 100) + '%"></span></div>' +
        '<h3 class="sf__q" tabindex="-1">' + esc(q) + '</h3>';
    }

    function backBtn() { return history.length ? '<button type="button" class="sf__back" data-sf-back>&larr; Back</button>' : ''; }

    function chemStep() {
      var html = stepHead('What will you spray?') + '<div class="sf__opts" role="group" aria-label="Common chemicals">';
      COMMON.forEach(function (c) { html += '<button type="button" class="sf__opt" data-sf-chem="' + esc(c[0]) + '">' + esc(c[1]) + '</button>'; });
      html += '<button type="button" class="sf__opt sf__opt--quiet" data-sf-unknown>Not sure</button></div>' +
        '<div class="sf__search"><label class="sf__label" for="' + root.id + '-q">Something else? Type the chemical or product</label>' +
        '<input type="text" class="sf__input" id="' + root.id + '-q" data-sf-q autocomplete="off" autocapitalize="none" spellcheck="false" placeholder="Try: brake cleaner, white spirit" value="' + esc(st.typed) + '">' +
        '<div class="sf__sugs" data-sf-sugs aria-live="polite"></div></div>';
      return html;
    }

    function strengthStep() {
      var row = E.byId(data, st.items[0].id);
      var pH = row.scale === 'pH';
      var unit = pH ? '' : '%';
      var html = stepHead('How strong is the ' + (row.short || row.name.replace(/\s*\(.*\)$/, '')).toLowerCase() + '?') +
        '<p class="sf__help">' + esc(row.scaleLabel || (pH ? 'The pH is on the label or the Safety Data Sheet (SDS).' : 'The strength is on the label or the Safety Data Sheet (SDS).')) + '</p>' +
        '<div class="sf__opts" role="group" aria-label="Strength">';
      var lo = null;
      row.bands.forEach(function (b) {
        var label = pH ? (lo === null ? 'pH up to ' + fmtNum(b.upto) : 'pH ' + fmtNum(lo) + ' to ' + fmtNum(b.upto))
          : (lo === null ? 'Up to ' + fmtNum(b.upto) + unit : fmtNum(lo) + ' to ' + fmtNum(b.upto) + unit);
        html += '<button type="button" class="sf__opt" data-sf-strength="' + b.upto + '" data-sf-band="' + esc(label) + '">' + esc(label) + '</button>';
        lo = b.upto;
      });
      if (row.over && lo !== null) {
        var over = pH ? 'Above pH ' + fmtNum(lo) : 'Over ' + fmtNum(lo) + unit;
        html += '<button type="button" class="sf__opt" data-sf-strength="' + (lo + 0.001) + '" data-sf-band="' + esc(over) + '">' + esc(over) + '</button>';
      }
      html += '<button type="button" class="sf__opt sf__opt--quiet" data-sf-strength="" data-sf-band="">Not sure</button></div>';
      return html + backBtn();
    }

    function formatStep() {
      var html = stepHead('How do you want to apply it?') + '<div class="sf__opts sf__opts--cards" role="group" aria-label="How you apply it">';
      FORMATS.forEach(function (f) {
        html += '<button type="button" class="sf__opt sf__opt--card" data-sf-format="' + f.v + '"><b>' + esc(f.label) + '</b><small>' + esc(f.hint) + '</small></button>';
      });
      return html + '</div>' + backBtn();
    }

    // ------------------------------------------------------------------ result

    function famModels(f) {
      var list = (data.models || []).filter(function (m) { return m.fam === f; });
      var fmt = st.format;
      if (fmt === 'foam') return list.filter(isFoam);
      if (fmt === 'mounted') return [];
      list = list.filter(function (m) { return !isFoam(m); });
      if (fmt === 'small') return list.filter(function (m) { var s = sizeOf(m); return s !== null && s < 3; });
      if (fmt === 'large') return list.filter(function (m) { var s = sizeOf(m); return s !== null && s >= 3; });
      return list;
    }

    function modelLinks(models) {
      return models.map(function (m) {
        return '<a class="sf__model" href="/products/' + esc(m.handle) + '"><span class="sf__brand">' + esc(m.brand) + '</span> ' + esc(m.name) + '</a>';
      }).join('');
    }

    function rangeFor(f) {
      if (st.format === 'foam') return { url: '/collections/' + FOAM_COL, name: 'foamer range' };
      if (st.format === 'mounted') return { url: '/collections/' + MOUNTED_COL, name: 'air driven and mounted range' };
      return { url: '/collections/' + data.families[f].col, name: data.families[f].label.toLowerCase() + ' range' };
    }

    var otherBrand = false;
    function brandNote() {
      var t = data.meta.models_text || {};
      return otherBrand && t.note ? '<p class="sf__fine">' + esc(t.note) + '</p>' : '';
    }

    // One sprayer: what to use, the models we stock in the chosen format, and the range. lead is extra
    // text (strength band, notes) shown between the answer and the models.
    function unitBlock(f, names, multi, lead) {
      var fam = data.families[f];
      var models = famModels(f);
      var range = rangeFor(f);
      var html = '<div class="sf__unit">';
      html += '<p class="sf__use">' + (multi ? '' : 'Use the ') + '<strong>' + esc(multi ? fam.label : fam.label.toLowerCase()) + '</strong> with <strong>' + esc(fam.seal) + '</strong> seals' +
        (names ? '<br><span class="sf__for">For: ' + esc(names) + '</span>' : '') + '</p>' + (lead || '');
      if (models.length) {
        html += '<p class="sf__mh">' + esc((data.meta.models_text && data.meta.models_text.head) || 'Models we stock') + '</p><div class="sf__models">' + modelLinks(models) + '</div>';
        if (models.some(function (m) { return m.brand !== 'Klager'; })) otherBrand = true;
      } else if (st.format === 'foam') {
        html += '<div class="sf__note">For this chemical, look for ' + esc(fam.seal) + ' seals. Our foamers come in several builds, so ask us and we will confirm the right one.</div>';
      } else if (st.format === 'mounted') {
        html += '<div class="sf__note">For this chemical, look for ' + esc(fam.seal) + ' seals. Our mounted and air driven units come in several builds, so ask us and we will confirm the right one.</div>';
      } else {
        html += '<div class="sf__note">We have no listed model in that size for this chemical yet. Ask us and we will match one.</div>';
      }
      html += '<a class="sf__link" href="' + esc(range.url) + '">View the ' + esc(range.name) + ' &rarr;</a></div>';
      return html;
    }

    function itemLabel(r) {
      var it = st.items.filter(function (i) { return i.id === r.id; })[0];
      var band = it && it.band ? it.band : (r.strength !== null && r.strength !== undefined ? r.strength + (r.scale === 'pH' ? ' pH' : '%') : '');
      return r.name + (band ? ', ' + band.charAt(0).toLowerCase() + band.slice(1) : '');
    }

    function badge(rating) {
      var label = (data.meta.rating_labels && data.meta.rating_labels[rating]) || rating;
      return '<span class="sf__badge sf__badge--' + esc(rating) + '">' + esc(label) + '</span>';
    }

    function formatText() {
      var f = FORMATS.filter(function (x) { return x.v === st.format; })[0];
      return f ? f.text : '';
    }

    function quoteHref(plan, kind) {
      var fmt = formatText();
      var rec;
      if (kind === 'unknown') rec = 'Chemical to confirm' + (fmt ? ', ' + fmt : '');
      else if (kind === 'typed') rec = 'Chemical not in the guide, please check';
      else if (plan.units.length) rec = plan.units.map(function (f) { return data.families[f].label; }).join(' and ') + (fmt ? ', ' + fmt : '');
      else rec = 'Check needed before buying' + (fmt ? ', ' + fmt : '');
      var chem = kind === 'typed' ? st.typed : kind === 'unknown' ? '' : plan.items.map(itemLabel).join('; ');
      var seal = plan && plan.sealGroups && plan.sealGroups.length ? plan.sealGroups.join(' and ') : '';
      return quoteBase + '?recommended=' + encodeURIComponent(rec) + (chem ? '&chemical=' + encodeURIComponent(chem) : '') + (seal ? '&seal=' + encodeURIComponent(seal) : '');
    }

    function guideLink(plan) {
      var list = E.encodeList(plan.items.map(function (r) { return { row: E.byId(data, r.id), strength: r.strength }; }));
      return guideUrl + '#guide&c=' + list + (plan.items.length > 1 ? '&m=list' : '') + (st.hot ? '&hot=1' : '');
    }

    function foot(href, label, extra) {
      return '<div class="sf__foot"><a class="sf__btn" data-sf-quote href="' + esc(href) + '">' + esc(label) + '</a>' + (extra || '') +
        '<span class="sf__again">' + (history.length ? '<button type="button" class="sf__back" data-sf-back>&larr; Back</button> ' : '') +
        '<button type="button" class="sf__back" data-sf-restart>Start again</button></span></div>';
    }

    function aiNote() {
      return st.via === 'assistant' && st.aiMessage ? '<p class="sf__msg" data-tone="' + esc(st.aiTone) + '">' + esc(st.aiMessage) + '</p>' : '';
    }

    function unknownResult() {
      var f = st.format;
      var range = f === 'foam' ? '/collections/' + FOAM_COL : f === 'mounted' ? '/collections/' + MOUNTED_COL : '/collections/chemical-resistant-sprayers';
      return '<div class="sf__card"><p class="sf__lead">No problem</p><h3 class="sf__rec">We will match the seal for you</h3>' +
        '<p>Send us the product name or its Safety Data Sheet (SDS). We check it against the makers’ data sheets and come back with the right sprayer and seal.</p>' +
        '<p><a class="sf__link" href="' + esc(range) + '">Browse the range &rarr;</a></p>' +
        foot(quoteHref(null, 'unknown'), 'Ask us to match it') + '</div>';
    }

    function result() {
      if (st.unknown) return unknownResult();
      var plan = resolved();
      otherBrand = false;
      var multi = plan.items.length > 1;
      var html = '<div class="sf__card">' + aiNote();
      if (!multi) {
        var r = plan.items[0];
        html += '<p class="sf__lead">Your answer</p><div class="sf__top"><h3 class="sf__rec">' + esc(itemLabel(r)) + '</h3>' + badge(r.rating) + '</div>';
        var lead = '';
        if (r.text) lead += '<p class="sf__band">' + esc(r.text) + '</p>';
        if (r.needsStrength) lead += '<p class="sf__band">The answer depends on the strength. Check the label or SDS, or ask us.</p>';
        if (r.note && !r.hot) lead += '<div class="sf__note">' + esc(r.note) + '</div>';
        if (r.fams.length > 1 && plan.units.length === 1) {
          lead += '<p class="sf__fine">Either the ' + r.fams.map(function (f) { return data.families[f].label.toLowerCase(); }).join(' or the ') + ' suits it.</p>';
        }
        if (r.fams.length) {
          html += unitBlock(r.assigned || r.fams[0], '', false, lead);
        } else {
          html += '<p class="sf__use"><strong>No standard sprayer is rated for this.</strong> Ask us before you buy.' + (r.spec ? ' ' + esc(r.spec) + '.' : '') + '</p>' + lead;
        }
      } else {
        var head = plan.units.length === 0 ? 'We need to check these with you' :
          plan.units.length === 1 && !plan.asks.length ? 'One sprayer covers your list' :
            plan.units.length === 1 ? 'One sprayer for most of your list' : 'You need ' + plan.units.length + ' sprayers';
        html += '<p class="sf__lead">Your answer</p><h3 class="sf__rec">' + esc(head) + '</h3>';
        plan.units.forEach(function (f) {
          var names = plan.covered.filter(function (r) { return r.assigned === f; }).map(itemLabel).join(', ');
          html += unitBlock(f, names, true);
        });
        if (plan.messages.length) html += '<ul class="sf__msgs">' + plan.messages.map(function (m) { return '<li>' + esc(m) + '</li>'; }).join('') + '</ul>';
      }
      html += brandNote() + '<p class="sf__fine">Filling liquid warmer than 30 &deg;C? Ask us first. ' + esc(data.meta.disclaimer) + '</p>';
      var label = plan.units.length ? 'Request a quote' : 'Ask us about this';
      html += foot(quoteHref(plan, plan.units.length ? 'plan' : 'ask'), label, '<a class="sf__link" href="' + esc(guideLink(plan)) + '">See the full answer and sources &rarr;</a>');
      return html + '</div>';
    }

    function typedResult(text, suggestions) {
      var clean = text.replace(/\s+/g, ' ').trim().slice(0, 80);
      st.typed = clean;
      var did = suggestions.slice(0, 3).map(function (s) { return '<button type="button" class="sf__sug" data-sf-chem="' + esc(s.row.id) + '">' + esc(s.row.name) + '</button>'; }).join('');
      return '<div class="sf__card sf__card--inline"><div class="sf__top"><h3 class="sf__rec">' + esc(clean) + '</h3>' + badge('ask') + '</div>' +
        '<p>We do not have a manufacturer rating for this, so we will not guess. Send us the Safety Data Sheet (SDS) and we will check it before you order.</p>' +
        (did ? '<p class="sf__mh">Did you mean</p><div class="sf__sugrow">' + did + '</div>' : '') +
        (endpoint ? '<p class="sf__fine">Or <button type="button" class="sf__textbtn" data-sf-goai>describe the job in your own words</button> and our AI helper will match it.</p>' : '') +
        '<div class="sf__foot"><a class="sf__btn" data-sf-quote href="' + esc(quoteHref(null, 'typed')) + '">Ask us about this chemical</a></div></div>';
    }

    // ------------------------------------------------------------------ drawing

    function draw(focus) {
      var step = current();
      var html;
      if (step === 'chem') html = chemStep();
      else if (step === 'strength') html = strengthStep();
      else if (step === 'format') html = formatStep();
      else html = result();
      if (step !== 'chem' && step !== 'result' && st.via === 'assistant' && st.aiMessage) html = aiMatched() + html;
      body.innerHTML = html;
      if (step === 'result') reportResult();
      if (focus) {
        var target = body.querySelector('.sf__q, .sf__rec');
        if (target) { if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1'); try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
        var top = root.getBoundingClientRect().top;
        if (top < 0 && root.scrollIntoView) root.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    function aiMatched() {
      var names = st.items ? st.items.map(function (i) { var row = E.byId(data, i.id); return row ? row.name + (typeof i.strength === 'number' ? ' (' + i.strength + (row.scale === 'pH' ? ' pH' : '%') + ')' : '') : ''; }).filter(Boolean) : [];
      return '<p class="sf__msg" data-tone="ok">' + (names.length ? 'We matched: <strong>' + esc(names.join(', ')) + '</strong>. ' : '') + 'One more question.</p>';
    }

    var reported = '';
    function reportResult() {
      var key = JSON.stringify([st.items, st.unknown, st.format]);
      if (key === reported) return;
      reported = key;
      if (st.unknown) { track('guide_result_shown', { guide_via: st.via, guide_chemical: 'not-sure', guide_list_size: 0 }); return; }
      var plan = resolved();
      track('guide_result_shown', {
        guide_via: st.via,
        guide_chemical: plan.items.map(function (r) { return r.id; }).join(','),
        guide_rating: plan.items.length === 1 ? plan.items[0].rating : '',
        guide_strength_given: plan.items.some(function (r) { return r.strength !== null && r.strength !== undefined; }) ? 1 : 0,
        guide_list_size: plan.items.length,
        guide_units_needed: plan.units.length,
        guide_ask_us: plan.asks.length,
      });
    }

    // Every answer moves one step on. The previous state goes on the stack for Back.
    function go(change) {
      history.push(snapshot());
      var n = st.n;
      change();
      st.n = n + 1;
      draw(true);
    }

    function restart() {
      history = [];
      st = fresh();
      reported = '';
      setTab('form');
      draw(true);
    }

    // ------------------------------------------------------------------ search on step 1

    var timer = null;
    function onType(input) {
      firstUse('form');
      clearTimeout(timer);
      timer = setTimeout(function () {
        load().then(function () { suggest(input.value); });
      }, 120);
    }

    function suggest(text) {
      var box = body.querySelector('[data-sf-sugs]');
      if (!box) return;
      st.typed = text;
      if (text.trim().length < 2) { box.innerHTML = ''; return; }
      var res = E.lookup(data, text);
      var rows = res.matches.map(function (m) { return m.row; });
      if (!rows.length) rows = startsWith(text);
      if (!rows.length) {
        box.innerHTML = '<p class="sf__fine">No match yet. Keep typing, or press Enter to ask us about it.</p>';
        return;
      }
      box.innerHTML = rows.slice(0, 5).map(function (row) {
        return '<button type="button" class="sf__sug" data-sf-chem="' + esc(row.id) + '">' + esc(row.name) + '<span>' + esc(data.groups[row.group] || '') + '</span></button>';
      }).join('');
    }

    // While the visitor is still typing a word ("brake clean"), offer the rows whose name or alias starts
    // with it. These are only suggestions to click; the answer always comes from the row picked.
    function startsWith(text) {
      var q = E.spell(E.parseQuery(text).text);
      if (q.length < 3) return [];
      return data.chemicals.filter(function (row) {
        return [row.name].concat(row.aka || []).some(function (l) {
          var n = E.spell(l);
          return n.indexOf(q) === 0 || n.indexOf(' ' + q) > -1;
        });
      });
    }

    function submitTyped(input) {
      load().then(function () {
        var text = input.value;
        if (text.trim().length < 2) return;
        var res = E.lookup(data, text);
        var strength = res.parsed.strength !== null ? res.parsed.strength : res.parsed.ph;
        if (res.matches.length && E.isClear(res)) {
          pickChem(res.matches[0].row.id, typeof strength === 'number' ? strength : null);
          return;
        }
        if (res.matches.length) { suggest(text); return; }
        history.push(snapshot());
        body.innerHTML = typedResult(text, res.suggestions) + backBtn();
        track('guide_result_shown', { guide_via: 'form', guide_chemical: 'not-in-guide', guide_rating: 'ask', guide_list_size: 0 });
      });
    }

    function pickChem(id, strength) {
      firstUse('form');
      load().then(function () {
        if (!E.byId(data, id)) return;
        go(function () {
          st.items = [{ id: id, strength: typeof strength === 'number' ? strength : null, band: '' }];
          st.unknown = false;
          st.strengthAsked = typeof strength === 'number';
          st.via = 'form';
          st.aiMessage = '';
        });
      });
    }

    // ------------------------------------------------------------------ assistant

    function setTab(which) {
      if (!aiPane) return;
      Array.prototype.forEach.call(tabs, function (t) {
        var on = t.getAttribute('data-sf-tab') === which;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      aiPane.hidden = which !== 'ai';
      if (formPane) formPane.hidden = which !== 'form';
      root.setAttribute('data-tab', which);
    }

    function aiSay(text, tone) {
      var msg = aiPane && aiPane.querySelector('[data-sf-aimsg]');
      if (!msg) return;
      msg.textContent = String(text || '').slice(0, 300);
      msg.setAttribute('data-tone', tone || '');
    }

    function aiHandle(b) {
      if (!b || typeof b !== 'object' || typeof b.kind !== 'string') {
        aiSay('Sorry, I could not look that up. Try the quick questions instead.', 'error');
        track('guide_assistant_use', { guide_kind: 'bad_reply', guide_rows: 0 });
        return;
      }
      var rows = Array.isArray(b.rows) ? b.rows.filter(function (r) { return r && typeof r.id === 'string'; }).slice(0, 8) : [];
      if ((b.kind === 'answer' || b.kind === 'safety') && rows.length) {
        load().then(function () {
          var items = [];
          rows.forEach(function (r) {
            if (E.byId(data, r.id) && !items.some(function (i) { return i.id === r.id; })) {
              items.push({ id: r.id, strength: typeof r.strength === 'number' && isFinite(r.strength) ? r.strength : null, band: '' });
            }
          });
          track('guide_assistant_use', { guide_kind: b.kind, guide_rows: items.length });
          if (!items.length) { aiSay('Sorry, I could not match that. Try the quick questions instead.', 'warn'); return; }
          history = [];
          st = fresh();
          st.items = items;
          st.via = 'assistant';
          st.hot = b.hot === true;
          st.aiMessage = String(b.message || '').slice(0, 300);
          st.aiTone = b.kind === 'safety' ? 'warn' : 'ok';
          st.strengthAsked = items.length !== 1 || items[0].strength !== null;
          st.n = 2;
          history.push({ aiPane: true });
          aiSay('');
          setTab('form');
          draw(true);
        });
        return;
      }
      track('guide_assistant_use', { guide_kind: b.kind, guide_rows: 0 });
      var tone = b.kind === 'ask_us' || b.kind === 'safety' || b.kind === 'busy' ? 'warn' : b.kind === 'error' ? 'error' : '';
      aiSay(b.message || 'Sorry, I could not look that up. Try the quick questions instead.', tone);
      var extra = aiPane.querySelector('[data-sf-aiextra]');
      if (extra) {
        extra.innerHTML = '';
        if (b.kind === 'ask_us') {
          var names = (Array.isArray(b.unmatched) ? b.unmatched : []).filter(function (n) { return typeof n === 'string'; }).slice(0, 5).join('; ');
          extra.innerHTML = '<a class="sf__link" data-sf-quote href="' + esc(quoteBase + '?recommended=' + encodeURIComponent('Chemical not in the guide, please check') + (names ? '&chemical=' + encodeURIComponent(names) : '')) + '">Ask us about it &rarr;</a>';
        } else if (b.kind !== 'answer') {
          extra.innerHTML = '<button type="button" class="sf__textbtn" data-sf-goform>Use the quick questions instead</button>';
        }
      }
    }

    function aiSubmit() {
      if (aiBusy || !endpoint) return;
      var input = aiPane.querySelector('[data-sf-aiq]');
      var btn = aiPane.querySelector('[data-sf-aigo]');
      var text = input.value.replace(/\s+/g, ' ').trim();
      if (text.length < 2) { aiSay('Type a short description of what you spray first.', 'warn'); return; }
      firstUse('assistant');
      aiBusy = true;
      btn.disabled = true;
      aiSay('Looking that up', '');
      var extra = aiPane.querySelector('[data-sf-aiextra]');
      if (extra) extra.innerHTML = '';
      load();
      var ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var t = setTimeout(function () { if (ctl) ctl.abort(); }, 12000);
      fetch(endpoint + '/api/assist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question: text.slice(0, 400) }),
        credentials: 'omit',
        signal: ctl ? ctl.signal : undefined,
      }).then(function (r) { return r.json(); }).then(aiHandle).catch(function () {
        aiSay('Sorry, I could not look that up. Try the quick questions instead.', 'error');
        track('guide_assistant_use', { guide_kind: 'network_error', guide_rows: 0 });
        if (extra) extra.innerHTML = '<button type="button" class="sf__textbtn" data-sf-goform>Use the quick questions instead</button>';
      }).then(function () {
        clearTimeout(t);
        aiBusy = false;
        btn.disabled = false;
      });
    }

    // ------------------------------------------------------------------ events

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;
      var el;
      if ((el = t.closest('[data-sf-tab]'))) { setTab(el.getAttribute('data-sf-tab')); if (el.getAttribute('data-sf-tab') === 'ai') load(); return; }
      if ((el = t.closest('[data-sf-chem]'))) {
        // A strength typed with the name ("hydrochloric acid 15%") carries over to the picked row.
        var qEl = body.querySelector('[data-sf-q]');
        var ps = qEl && qEl.value ? E.parseQuery(qEl.value) : null;
        var s = ps ? (ps.strength !== null ? ps.strength : ps.ph) : null;
        pickChem(el.getAttribute('data-sf-chem'), typeof s === 'number' ? s : null);
        return;
      }
      if ((el = t.closest('[data-sf-unknown]'))) { firstUse('form'); load().then(function () { go(function () { st.unknown = true; st.items = null; }); }); return; }
      if ((el = t.closest('[data-sf-strength]'))) {
        var v = el.getAttribute('data-sf-strength');
        var band = el.getAttribute('data-sf-band');
        go(function () { st.items[0].strength = v === '' ? null : parseFloat(v); st.items[0].band = band; st.strengthAsked = true; });
        return;
      }
      if ((el = t.closest('[data-sf-format]'))) { var f = el.getAttribute('data-sf-format'); go(function () { st.format = f; }); return; }
      if ((el = t.closest('[data-sf-back]'))) {
        var prev = history.pop();
        if (prev) {
          if (prev.aiPane) { restart(); setTab('ai'); return; }
          st = prev;
          reported = '';
        }
        draw(true);
        return;
      }
      if ((el = t.closest('[data-sf-restart]'))) { restart(); return; }
      if ((el = t.closest('[data-sf-goai]'))) { setTab('ai'); var q = aiPane && aiPane.querySelector('[data-sf-aiq]'); if (q) { if (!q.value) q.value = st.typed; q.focus(); } return; }
      if ((el = t.closest('[data-sf-goform]'))) { restart(); return; }
      if ((el = t.closest('[data-sf-aigo]'))) { aiSubmit(); return; }
      if ((el = t.closest('[data-sf-quote]'))) {
        var plan = data && st.items ? resolved() : null;
        track('guide_quote_click', { guide_via: st.via, guide_list_size: plan ? plan.items.length : 0, guide_units_needed: plan ? plan.units.length : 0 });
      }
    });

    root.addEventListener('input', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-sf-q')) onType(e.target);
    });
    root.addEventListener('keydown', function (e) {
      var t = e.target;
      if (t.hasAttribute && t.hasAttribute('data-sf-q') && e.key === 'Enter') { e.preventDefault(); submitTyped(t); return; }
      if (t.hasAttribute && t.hasAttribute('data-sf-aiq') && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); aiSubmit(); return; }
      if (t.hasAttribute && t.hasAttribute('data-sf-tab') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        var next = t.getAttribute('data-sf-tab') === 'form' ? 'ai' : 'form';
        setTab(next);
        var nt = root.querySelector('[data-sf-tab="' + next + '"]');
        if (nt) nt.focus();
      }
    });

    ['pointerenter', 'focusin', 'touchstart'].forEach(function (ev) { root.addEventListener(ev, function () { load(); }, { once: true, passive: true }); });

    if (aiPane && !endpoint) {
      // No usable address: hide the AI tab and keep the quick questions.
      var bar = root.querySelector('[data-sf-tabs]');
      if (bar) bar.hidden = true;
      aiPane.hidden = true;
    }
    if (aiPane && endpoint) setTab('form');
    draw(false);
  }

  var roots = document.querySelectorAll('[data-sf-root]');
  for (var i = 0; i < roots.length; i += 1) init(roots[i]);
})();
